/**
 * Approval consent page — /approve/:approvalId (OPP-133).
 *
 * A service creates an approval (POST /v1/auth/approvals) and sends the user
 * here. The user can switch freely between:
 *   - passkey on this device
 *   - passkey on their phone (browser shows a QR, hybrid transport)
 *   - push to the Authenticator app (this page keeps polling)
 * Whichever resolves first wins; the gateway rejects the rest.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { buildLoginUrl } from '../lib/kratos';
import { getAssertion, canUsePhonePasskey, PHONE_PASSKEY_UNAVAILABLE, type AuthenticatorMode } from '../lib/webauthn';
import { config } from '../config';
import LoadingSpinner from '../components/LoadingSpinner';

interface Passkey {
  id: string;
  name: string;
  added_at: string | null;
}

interface ApprovalView {
  approval_id: string;
  org_id: string;
  approval_type: string;
  payload: Record<string, unknown>;
  status: string;
  expires_at: string;
  passkeys: Passkey[];
  push_available: boolean;
}

type Outcome = { status: 'approved' | 'denied' | 'expired'; method?: string | null };

const POLL_MS = 2000;

const TYPE_LABELS: Record<string, string> = {
  payment_approval: 'Approve payment',
  login_approval: 'Approve sign-in',
  action_approval: 'Approve action',
  security_alert: 'Security confirmation',
};

const METHOD_LABELS: Record<string, string> = {
  passkey: 'with your passkey',
  push: 'from the Authenticator app',
  web: 'from this browser',
};

async function errorDetail(resp: Response, fallback: string): Promise<string> {
  try {
    const body = await resp.json();
    return typeof body.detail === 'string' ? body.detail : fallback;
  } catch {
    return fallback;
  }
}

export default function Approve() {
  const { approvalId = '' } = useParams();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const [view, setView] = useState<ApprovalView | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<AuthenticatorMode | 'push' | 'deny' | null>(null);
  const [pushSent, setPushSent] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string>('');
  const pollRef = useRef<number | null>(null);
  const [phonePasskey, setPhonePasskey] = useState<boolean | null>(null);

  useEffect(() => { void canUsePhonePasskey().then(setPhonePasskey); }, []);

  const base = `${config.gatewayUrl}/v1/auth/approvals/${encodeURIComponent(approvalId)}`;

  const load = useCallback(async () => {
    const resp = await fetch(`${base}/web`, { credentials: 'include' });
    if (resp.status === 409 || resp.status === 410) {
      // Already resolved or expired — show the final state
      const s = await fetch(`${base}/status`, { credentials: 'include' });
      if (s.ok) {
        const body = await s.json();
        setOutcome({ status: body.status, method: body.resolution_method });
        return;
      }
    }
    if (!resp.ok) {
      setError(await errorDetail(resp, 'Unable to load this approval.'));
      return;
    }
    setView(await resp.json());
  }, [base]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      window.location.href = buildLoginUrl(`${window.location.pathname}${window.location.search}`);
      return;
    }
    load();
  }, [authLoading, isAuthenticated, load]);

  // Poll while pending so a push approval on the phone lands here too
  useEffect(() => {
    if (!view || outcome) return;
    pollRef.current = window.setInterval(async () => {
      try {
        const resp = await fetch(`${base}/status`, { credentials: 'include' });
        if (!resp.ok) return;
        const body = await resp.json();
        if (body.status !== 'pending') setOutcome({ status: body.status, method: body.resolution_method });
      } catch {
        // transient — keep polling
      }
    }, POLL_MS);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [view, outcome, base]);

  const approveWithPasskey = async (mode: AuthenticatorMode) => {
    setBusy(mode);
    setError(null);
    try {
      const optResp = await fetch(`${base}/webauthn/options`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode, credential_id: selectedKey || null }),
      });
      if (!optResp.ok) throw new Error(await errorDetail(optResp, 'Could not start passkey approval.'));
      const { publicKey } = await optResp.json();

      const credential = await getAssertion(publicKey, mode);

      const verifyResp = await fetch(`${base}/webauthn/verify`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential }),
      });
      if (!verifyResp.ok) throw new Error(await errorDetail(verifyResp, 'Passkey verification failed.'));
      const body = await verifyResp.json();
      setOutcome({ status: body.status, method: body.resolution_method });
    } catch (err) {
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        setError('Passkey prompt was cancelled or timed out. You can try again or use another method.');
      } else {
        setError(err instanceof Error ? err.message : 'Passkey approval failed.');
      }
    } finally {
      setBusy(null);
    }
  };

  const sendToPhone = async () => {
    setBusy('push');
    setError(null);
    try {
      const resp = await fetch(`${base}/push`, { method: 'POST', credentials: 'include' });
      if (!resp.ok) throw new Error(await errorDetail(resp, 'Could not send to your phone.'));
      setPushSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send to your phone.');
    } finally {
      setBusy(null);
    }
  };

  const deny = async () => {
    setBusy('deny');
    setError(null);
    try {
      const resp = await fetch(`${base}/deny`, { method: 'POST', credentials: 'include' });
      if (!resp.ok) throw new Error(await errorDetail(resp, 'Could not deny.'));
      const body = await resp.json();
      setOutcome({ status: body.status, method: body.resolution_method });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not deny.');
    } finally {
      setBusy(null);
    }
  };

  if (authLoading || (!view && !outcome && !error)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading approval..." />
      </div>
    );
  }

  const hasPasskeys = (view?.passkeys.length ?? 0) > 0;
  const payloadEntries = Object.entries(view?.payload ?? {}).filter(
    ([, v]) => ['string', 'number', 'boolean'].includes(typeof v),
  );

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="card max-w-md w-full">
        <h1 className="text-xl font-semibold text-content-primary mb-1">
          {view ? TYPE_LABELS[view.approval_type] ?? 'Approval required' : 'Approval'}
        </h1>

        {outcome ? (
          <div className="mt-4 text-center space-y-2">
            <p className={`text-lg font-medium ${outcome.status === 'approved' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {outcome.status === 'approved' ? 'Approved' : outcome.status === 'denied' ? 'Denied' : 'Expired'}
            </p>
            {outcome.method && outcome.status !== 'expired' && (
              <p className="text-sm text-content-secondary">{METHOD_LABELS[outcome.method] ?? ''}</p>
            )}
            <p className="text-sm text-content-tertiary">You can close this window.</p>
          </div>
        ) : view ? (
          <div className="space-y-4">
            {view.org_id && <p className="text-sm text-content-tertiary">Organization: {view.org_id}</p>}

            {payloadEntries.length > 0 && (
              <dl className="border rounded-lg divide-y text-sm">
                {payloadEntries.map(([k, v]) => (
                  <div key={k} className="flex justify-between px-4 py-2 gap-4">
                    <dt className="text-content-tertiary capitalize">{k.replace(/_/g, ' ')}</dt>
                    <dd className="text-content-primary font-medium text-right break-all">{String(v)}</dd>
                  </div>
                ))}
              </dl>
            )}

            {error && (
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-sm text-red-700 dark:text-red-300">{error}</div>
            )}

            {hasPasskeys && view.passkeys.length > 1 && (
              <div>
                <label htmlFor="passkey-select" className="block text-sm font-medium text-content-primary mb-1">
                  Passkey
                </label>
                <select
                  id="passkey-select"
                  className="input-field"
                  value={selectedKey}
                  onChange={(e) => setSelectedKey(e.target.value)}
                >
                  <option value="">Any of my passkeys</option>
                  {view.passkeys.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            )}

            {hasPasskeys && (
              <>
                <button
                  type="button"
                  className="btn-primary w-full"
                  disabled={busy !== null}
                  onClick={() => approveWithPasskey('device')}
                >
                  {busy === 'device' ? <LoadingSpinner size="sm" /> : 'Approve with passkey on this device'}
                </button>
                {phonePasskey === false ? (
                  <p className="text-xs text-content-tertiary text-center">{PHONE_PASSKEY_UNAVAILABLE}</p>
                ) : (
                  <button
                    type="button"
                    className="btn-secondary w-full"
                    disabled={busy !== null}
                    onClick={() => approveWithPasskey('phone')}
                  >
                    {busy === 'phone' ? <LoadingSpinner size="sm" /> : 'Approve with passkey on my phone (scan QR)'}
                  </button>
                )}
              </>
            )}

            {view.push_available && (
              <button
                type="button"
                className="btn-secondary w-full"
                disabled={busy !== null}
                onClick={sendToPhone}
              >
                {busy === 'push' ? <LoadingSpinner size="sm" /> : pushSent ? 'Resend to Authenticator app' : 'Send to Authenticator app'}
              </button>
            )}
            {pushSent && (
              <p className="text-sm text-content-secondary text-center">
                Waiting for your phone… you can still use a passkey here instead.
              </p>
            )}

            {!hasPasskeys && !view.push_available && (
              <p className="text-sm text-content-secondary">
                No passkey or Authenticator app is set up. Add a passkey in Settings → Security.
              </p>
            )}

            <button
              type="button"
              className="w-full text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 py-2"
              disabled={busy !== null}
              onClick={deny}
            >
              Deny
            </button>

            <p className="text-xs text-content-tertiary text-center">
              Expires {new Date(view.expires_at).toLocaleTimeString()}
            </p>
          </div>
        ) : (
          error && <p className="text-sm text-red-600 dark:text-red-400 mt-4">{error}</p>
        )}
      </div>
    </div>
  );
}
